// Édition des étapes d'UN dossier (la copie du dossier, jamais le modèle).
// Mêmes règles que la RPC `edit_dossier_frise_steps`.

export const FRISE_MIN_STEPS = 2;
export const FRISE_MAX_STEPS = 20;
export const FRISE_LABEL_MAX = 80;

export interface FriseEditRow {
  /** Position d'origine, ou null pour une étape ajoutée. */
  from: number | null;
  label: string;
}

export interface FriseEditContext {
  /** Position de l'étape courante du dossier. */
  currentPosition: number;
  /** Position de la dernière étape (clôture). */
  lastPosition: number;
}

export function cleanStepLabel(label: string): string {
  return label.replace(/\s+/g, ' ').trim();
}

/** L'étape courante et l'étape de clôture ne se retirent pas. */
export function canRemoveStep(row: FriseEditRow, ctx: FriseEditContext): boolean {
  return row.from === null || (row.from !== ctx.currentPosition && row.from !== ctx.lastPosition);
}

/** La clôture reste la dernière : elle ne bouge pas, et rien ne passe après elle. */
export function canMoveStep(
  rows: FriseEditRow[],
  index: number,
  direction: -1 | 1,
  ctx: FriseEditContext,
): boolean {
  const target = index + direction;
  if (target < 0 || target >= rows.length) return false;
  return rows[index]?.from !== ctx.lastPosition && rows[target]?.from !== ctx.lastPosition;
}

export function moveStep(rows: FriseEditRow[], index: number, direction: -1 | 1): FriseEditRow[] {
  const next = [...rows];
  const target = index + direction;
  [next[index], next[target]] = [next[target]!, next[index]!];
  return next;
}

/** Ajoute une étape vide juste avant la clôture. */
export function addStep(rows: FriseEditRow[]): FriseEditRow[] {
  return [...rows.slice(0, -1), { from: null, label: '' }, ...rows.slice(-1)];
}

/** Message d'erreur en français, ou null si la liste peut être enregistrée. */
export function validateFriseEdit(rows: FriseEditRow[], ctx: FriseEditContext): string | null {
  if (rows.length < FRISE_MIN_STEPS) return 'Il faut au moins deux étapes.';
  if (rows.length > FRISE_MAX_STEPS) return `Pas plus de ${FRISE_MAX_STEPS} étapes.`;
  for (const row of rows) {
    const label = cleanStepLabel(row.label);
    if (!label) return 'Chaque étape doit avoir un nom.';
    if (label.length > FRISE_LABEL_MAX) return `Un nom d’étape fait ${FRISE_LABEL_MAX} caractères au plus.`;
  }
  if (!rows.some((row) => row.from === ctx.currentPosition)) {
    return 'L’étape en cours ne peut pas être retirée.';
  }
  if (rows[rows.length - 1]?.from !== ctx.lastPosition) {
    return 'La dernière étape doit rester la dernière.';
  }
  return null;
}

export function isFriseEditDirty(
  rows: FriseEditRow[],
  original: { position: number; label: string }[],
): boolean {
  if (rows.length !== original.length) return true;
  return rows.some(
    (row, i) => row.from !== original[i]!.position || cleanStepLabel(row.label) !== original[i]!.label,
  );
}

export function toFriseEditPayload(rows: FriseEditRow[]): { from: number | null; label: string }[] {
  return rows.map((row) => ({ from: row.from, label: cleanStepLabel(row.label) }));
}
